import { useState, useEffect } from 'react'
import { api } from '../api/client'

/**
 * Hook to fetch a single collection item directly from the API
 * Replaces the cache-based useCollectionItem from the deleted useCollectionItems hook
 */
export function useCollectionItem(collectionId: number, itemId: string | number) {
    const [item, setItem] = useState<any>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    // Bumped by `refetch` so callers that edit the item (and don't have a
    // page-level cache to invalidate) can pull the saved row back in.
    const [reloadToken, setReloadToken] = useState(0)

    useEffect(() => {
        let isMounted = true

        async function fetchItem() {
            try {
                setLoading(true)
                setError(null)

                const response = await api.get(`/api/collections/${collectionId}/items/${itemId}`)

                if (isMounted) {
                    setItem(response.data)
                }
            } catch (err) {
                if (isMounted) {
                    setError('Failed to fetch collection item')
                    console.error('Error fetching collection item:', err)
                }
            } finally {
                if (isMounted) {
                    setLoading(false)
                }
            }
        }

        if (collectionId && itemId) {
            fetchItem()
        }

        return () => {
            isMounted = false
        }
    }, [collectionId, itemId, reloadToken])

    return { item, loading, error, refetch: () => setReloadToken((t) => t + 1) }
}

/**
 * Hook to fetch a collection schema directly from the API
 * Replaces getCollectionFromCache from the deleted useCollectionItems hook
 */
export function useCollection(collectionId: number) {
    const [collection, setCollection] = useState<any>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        let isMounted = true

        async function fetchCollection() {
            try {
                setLoading(true)
                setError(null)

                const response = await api.get(`/api/collections/${collectionId}`)

                if (isMounted) {
                    setCollection(response.data)
                }
            } catch (err) {
                if (isMounted) {
                    setError('Failed to fetch collection')
                    console.error('Error fetching collection:', err)
                }
            } finally {
                if (isMounted) {
                    setLoading(false)
                }
            }
        }

        if (collectionId) {
            fetchCollection()
        }

        return () => {
            isMounted = false
        }
    }, [collectionId])

    return { collection, loading, error }
}